import { AxiosResponse } from "axios";

import { API } from "../index";

export type NotificationCategory =
  | "payment"
  | "usage"
  | "site_notice"
  | "marketing"
  | "mandatory";

export type NotificationChannel = "web_push" | "app_push" | "alimtalk";

export type AlimTalkAccountMode = "HEADQUARTERS" | "DIRECT";

export type AlimTalkTemplateVariable = {
  key: string;
  label?: string;
  description?: string;
  required?: boolean;
  source?: "manual" | "recipient" | "system" | string;
};

export type AlimTalkButton = {
  name: string;
  type: "WL" | "AL" | "DS" | "BK" | "MD" | "AC";
  url_mobile?: string;
  url_pc?: string;
  scheme_android?: string;
  scheme_ios?: string;
};

export type AlimTalkTemplate = {
  id: number;
  site_id?: number | null;
  sender_id?: number | null;
  template_code?: string;
  template_name?: string;
  name?: string;
  template_content?: string;
  content?: string;
  template_type?: string;
  category_code?: string;
  template_status?: string;
  status?: string;
  is_archived?: boolean;
  is_secure_template?: boolean;
  buttons?: AlimTalkButton[];
  variables?: Array<string | AlimTalkTemplateVariable>;
  ordered_variables?: string[];
  scope?: "HEADQUARTERS" | "SITE" | string;
  editable?: boolean;
  revision?: number;
  created_at?: string;
  updated_at?: string;
};

export type AlimTalkWallet = {
  id?: number | null;
  site_id?: number;
  balance?: number | string;
  available_balance?: number | string;
  pending_amount?: number | string;
  reserved_balance?: number | string;
  pending_topup_count?: number;
  currency?: string;
  low_balance_threshold?: number | null;
  updated_at?: string | null;
};

export type AlimTalkTopupStatus =
  | "PENDING"
  | "APPROVED"
  | "REJECTED"
  | "CANCELLED"
  | "pending"
  | "approved"
  | "rejected"
  | "cancelled";

export type AlimTalkTopupRequest = {
  id: number;
  site_id?: number;
  site_name?: string;
  requested_amount?: number | string;
  amount?: number | string;
  approved_amount?: number | string | null;
  depositor_name?: string;
  bank_name?: string;
  bank_account?: string;
  bank_snapshot?: Record<string, unknown>;
  note?: string;
  memo?: string;
  expected_deposit_date?: string | null;
  status: AlimTalkTopupStatus | string;
  requested_by_name?: string;
  requested_by_id?: number | null;
  reviewed_by_name?: string;
  reviewed_by_id?: number | null;
  review_memo?: string;
  rejection_reason?: string;
  created_at?: string;
  reviewed_at?: string | null;
  wallet_entry_id?: number | null;
};

export type AlimTalkBillingSetting = {
  bank_name?: string;
  bank_account?: string;
  bank_holder?: string;
  account_number?: string;
  account_holder?: string;
  guidance?: string;
  enforcement_enabled?: boolean;
  alimtalk_unit_price?: number;
  currency?: string;
  is_enabled?: boolean;
  updated_at?: string;
};

export type AlimTalkMessageRate = {
  id: number;
  site: number | null;
  site_name?: string | null;
  channel: "AT" | "AI" | string;
  source?: string | null;
  unit_price: number | string;
  currency: string;
  is_active: boolean;
  effective_from: string;
  effective_to?: string | null;
  reason: string;
  created_at?: string;
};

export type NotificationListQuery = { page?: number; page_size?: number; status?: string; search?: string; created_from?: string; created_to?: string };

export type AlimTalkWalletEntry = {
  id: number; entry_type: string; available_delta: number; reserved_delta: number;
  available_after: number; reserved_after: number; topup_request_id: number | null;
  charge_id: number | null; message_id: number | null; notification_id: string | null;
  reason: string; created_at: string;
};

export type Delivery = {
  id: number;
  channel: NotificationChannel;
  provider: string;
  provider_message_id?: string;
  result_detail?: { state?: string; code?: string; completed_at?: string | null; checked_at?: string };
  delivered_at?: string | null;
  target_label: string;
  state: string;
  error_message: string;
  attempted_at: string | null;
  accepted_at: string | null;
};

export type NotificationHistoryItem = {
  id: number;
  user_id: number | null;
  user_name: string;
  phone_num: string;
  category: NotificationCategory;
  event_type: string;
  title: string;
  body: string;
  status: string;
  created_at: string;
  read_at: string | null;
  deliveries: Delivery[];
  dispatch_state?: string;
};

export type CategorySetting = {
  category: NotificationCategory;
  is_enabled: boolean;
  web_push_enabled: boolean;
  app_push_enabled: boolean;
  alimtalk_enabled: boolean;
  channel_priority: NotificationChannel[];
  is_default?: boolean;
};

export type EventPolicy = {
  id?: number;
  event_type: string;
  category: NotificationCategory;
  is_enabled: boolean;
  web_push_enabled: boolean;
  app_push_enabled: boolean;
  alimtalk_enabled: boolean;
  alimtalk_template_id: number | null;
  alimtalk_template_name: string;
  alimtalk_variable_bindings?: Record<string, string>;
  alimtalk_template_revision?: number;
  channel_priority: NotificationChannel[];
};

export type NotificationEventCatalogItem = {
  event_type: string;
  label?: string;
  description?: string;
  category: NotificationCategory;
  variables?: Array<string | AlimTalkTemplateVariable>;
  default_policy?: Partial<EventPolicy>;
};

export type NotificationSettingsResponse = {
  site_id: number;
  settings: CategorySetting[];
  event_policies: EventPolicy[];
  event_catalog?: NotificationEventCatalogItem[];
};

export type EventPolicyUpdatePayload = Pick<
  EventPolicy,
  | "event_type"
  | "category"
  | "is_enabled"
  | "web_push_enabled"
  | "app_push_enabled"
  | "alimtalk_enabled"
  | "alimtalk_template_id"
  | "alimtalk_variable_bindings"
  | "channel_priority"
>;

export type NotificationSendPayload = {
  recipient_ids: number[];
  category: NotificationCategory;
  event_type?: string;
  title: string;
  body: string;
  web_url?: string;
  app_url?: string;
  data?: Record<string, unknown>;
  channels: NotificationChannel[];
  recipient_channel_overrides?: Array<{
    recipient_id: number;
    channels: NotificationChannel[];
  }>;
  alimtalk_template_id?: number | null;
  alimtalk_variables?: Record<string, string>;
  preflight_token?: string;
};

export type EventParticipationSummary = { event_name: string; new_count: number; existing_count: number };
export type NotificationEventParticipant = {
  id: number; event_name: string; user_id: number; name: string; phone_num: string | null;
  confirmed_by_name: string; confirmed_at: string; first_notification_id: number | null;
};

export type RecipientChannelAvailability = {
  available: boolean;
  reason: string;
};

export type NotificationDeliveryCheckRecipient = {
  id: number;
  name: string;
  phone_num: string;
  site_registered: boolean;
  category_enabled: boolean;
  reason: string;
  channels: Record<NotificationChannel, RecipientChannelAvailability>;
};

export type NotificationDeliveryCheckResponse = {
  category: NotificationCategory;
  site_channels: Record<NotificationChannel, {
    site_enabled: boolean;
    configured: boolean;
    reason: string;
  }>;
  alimtalk_templates: AlimTalkTemplate[];
  selected_alimtalk_template_id: number | null;
  alimtalk_cost?: {
    unit_price?: number;
    recipient_count?: number;
    estimated_amount?: number;
    currency?: string;
  } | null;
  alimtalk_balance?: AlimTalkWallet | null;
  alimtalk_preview?: string;
  server_preview?: {
    recipient_id?: number;
    content?: string;
    buttons?: AlimTalkButton[];
  } | null;
  preview_error?: string;
  unit_price?: number | string | null;
  max_estimated_charge?: number | string | null;
  credit_sufficient?: boolean | null;
  credit_enforced?: boolean;
  wallet?: AlimTalkWallet | null;
  preflight_token?: string;
  recipients: NotificationDeliveryCheckRecipient[];
};

export type AlimTalkSender = {
  id: number;
  profile_name: string;
  business_name: string;
  business_number: string;
  sender_no: string;
  status: string;
};

export type AlimTalkAccountResponse = {
  site_id: number;
  configured: boolean;
  provider_configured: boolean;
  sender_number_configured?: boolean;
  delivery_ready: boolean;
  using_default: boolean;
  sender_key_hint: string;
  sender: AlimTalkSender | null;
  errors: string[];
  account_mode?: AlimTalkAccountMode;
  scope?: "HEADQUARTERS" | "SITE";
  requires_prepaid_credit?: boolean;
  wallet?: AlimTalkWallet | null;
  capabilities?: {
    account_mode?: AlimTalkAccountMode;
    scope?: "HEADQUARTERS" | "SITE";
    uses_headquarters_account?: boolean;
    requires_prepaid_credit?: boolean;
    connection_managed_by_site?: boolean;
    template_write_role?: "MASTER" | "OWNER" | string;
    can_change_mode?: boolean;
    can_edit_connection?: boolean;
    can_manage_templates?: boolean;
    can_request_topup?: boolean;
    can_manage_topups?: boolean;
  };
};

export type AlimTalkAccountUpdatePayload = {
  account_mode?: AlimTalkAccountMode;
  send_profile_key?: string;
  profile_name?: string;
  business_name?: string;
  business_number?: string;
  sender_no?: string;
};

export type HeadquartersAlimTalkSenderResponse = {
  configured: boolean;
  provider_configured: boolean;
  scope: "HEADQUARTERS";
  sender: AlimTalkSender | null;
  sender_key_hint: string;
  sender_number_configured: boolean;
  errors: string[];
};

export type HeadquartersAlimTalkSenderUpdatePayload = {
  sender_no: string;
};

export type AlimTalkTemplateUpsertPayload = {
  template_code?: string;
  template_name: string;
  template_content: string;
  template_type?: string;
  category_code?: string;
  is_secure_template?: boolean;
  buttons?: AlimTalkButton[];
};

export type AlimTalkProviderTemplateCatalogItem = {
  template_code: string;
  template_name: string;
  template_content: string;
  template_type: string;
  category_code: string;
  status: string;
  buttons: AlimTalkButton[];
  ordered_variables: string[];
  local_template_id: number | null;
  local_status: string;
  importable: boolean;
};

export type AlimTalkTemplateBlueprint = AlimTalkTemplateUpsertPayload & {
  event_type: string;
  event_label: string;
  category: NotificationCategory;
  template_code: string;
  template_type: string;
  category_code: string;
  is_secure_template: boolean;
  buttons: AlimTalkButton[];
  ordered_variables: string[];
};

export type AlimTalkWalletResponse = {
  site_id: number;
  account_mode?: AlimTalkAccountMode;
  wallet: AlimTalkWallet | null;
  billing_setting?: AlimTalkBillingSetting;
  entries?: Array<Record<string, unknown>>;
} & Partial<AlimTalkWallet>;

export type NotificationRecipient = { id: number; name: string; phone_num: string | null; last_activity_at: string | null };
export type NotificationBenefitKind = "coupon" | "voucher" | "precoupon";
export type NotificationBenefitLink = {
  id: string; kind: NotificationBenefitKind; source: "event" | "shared";
  name: string; url: string; end_date: string | null; per_person_limit: number; remaining_stock: number | null;
};
export type NotificationRecipientQuery = {
  mode: "individual" | "recent_access" | "recent_purchase";
  search?: string; start_date?: string; end_date?: string; page?: number; page_size?: number;
};

export const notificationCenterService = {
  getEventParticipants: (params: { event_name: string; page: number; page_size: number }) => API.get<{ count: number; results: NotificationEventParticipant[] }>("/api/v2/notifications/event-participants/", { params }),
  getComposerSetting: () => API.get<{ configured: boolean; visible_template_ids: number[] }>("/api/v2/notifications/composer-settings/"),
  saveComposerSetting: (visible_template_ids: number[]) => API.put<{ configured: boolean; visible_template_ids: number[] }>("/api/v2/notifications/composer-settings/", { visible_template_ids }),
  getBenefitLinks: (params: { kind: NotificationBenefitKind; search?: string; page: number; page_size: number }) => API.get<{ count: number; results: NotificationBenefitLink[] }>("/api/v2/notifications/benefit-links/", { params }),
  getRecipients: (params: NotificationRecipientQuery) => API.get<{ count: number; results: NotificationRecipient[] }>("/api/v2/notifications/recipients/", { params }),
  selectAllRecipients: (params: NotificationRecipientQuery) => API.get<{ count: number; recipient_ids: number[] }>("/api/v2/notifications/recipients/", { params: { ...params, selection: "all" } }),

  getHistory: async (params?: NotificationListQuery & {
    channel?: string; state?: string; dispatch_state?: string; notification_id?: string;
    category?: NotificationCategory | "";
    search?: string;
  }): Promise<AxiosResponse<{ count: number; results: NotificationHistoryItem[] }>> => {
    return API.get("/api/v2/notifications/history/", { params });
  },

  syncAlimTalkResult: (id: number) => API.post<NotificationHistoryItem>(`/api/v2/notifications/history/${id}/sync-alimtalk/`),

  getAlimTalkWalletEntries: (params?: NotificationListQuery & { entry_type?: string }) => API.get<{ count: number; results: AlimTalkWalletEntry[] }>("/api/v2/notifications/alimtalk-wallet/entries/", { params }),

  // 결정 262·388: 관리자 수동 알림톡 발송 잠금 상태(대표님 '시작' 전까지 locked)
  getSendLock: () => API.get<{ locked: boolean; reason: string }>("/api/v2/notifications/send-lock/"),

  send: async (payload: NotificationSendPayload): Promise<AxiosResponse<{ created: number; notifications: NotificationHistoryItem[]; event_participation?: EventParticipationSummary }>> => {
    return API.post("/api/v2/notifications/send/", payload);
  },

  checkDelivery: async (payload: {
    recipient_ids: number[];
    category: NotificationCategory;
    alimtalk_template_id?: number | null;
    alimtalk_variables?: Record<string, string>;
    channels?: NotificationChannel[];
    recipient_channel_overrides?: Array<{
      recipient_id: number;
      channels: NotificationChannel[];
    }>;
  }): Promise<AxiosResponse<NotificationDeliveryCheckResponse>> => {
    return API.post("/api/v2/notifications/delivery-check/", payload);
  },

  getSiteSettings: async (): Promise<AxiosResponse<NotificationSettingsResponse>> => {
    return API.get("/api/v2/notifications/site-settings/");
  },

  updateSiteSettings: async (payload: {
    settings?: CategorySetting[];
    event_policies?: EventPolicyUpdatePayload[];
  }): Promise<AxiosResponse<NotificationSettingsResponse>> => {
    return API.patch("/api/v2/notifications/site-settings/", payload);
  },

  getWebPushConfig: async (): Promise<AxiosResponse<{ is_configured: boolean; public_key: string | null }>> => {
    return API.get("/api/v2/notifications/web-push/config/");
  },

  getAlimTalkAccount: async (): Promise<AxiosResponse<AlimTalkAccountResponse>> => {
    return API.get("/api/v2/notifications/alimtalk-account/");
  },

  updateAlimTalkAccount: async (
    payload: AlimTalkAccountUpdatePayload,
  ): Promise<AxiosResponse<AlimTalkAccountResponse>> => {
    return API.patch("/api/v2/notifications/alimtalk-account/", payload);
  },

  disconnectAlimTalkAccount: async (): Promise<AxiosResponse<AlimTalkAccountResponse>> => {
    return API.delete("/api/v2/notifications/alimtalk-account/");
  },

  getAdminAlimTalkSender: async (): Promise<AxiosResponse<HeadquartersAlimTalkSenderResponse>> => {
    return API.get("/api/v2/notifications/admin/alimtalk-sender/");
  },

  updateAdminAlimTalkSender: async (
    payload: HeadquartersAlimTalkSenderUpdatePayload,
  ): Promise<AxiosResponse<HeadquartersAlimTalkSenderResponse>> => {
    return API.patch("/api/v2/notifications/admin/alimtalk-sender/", payload);
  },

  getAlimTalkTemplates: async (params?: {
    page?: number;
    page_size?: number;
    status?: string;
    search?: string;
  }): Promise<AxiosResponse<{ count: number; results: AlimTalkTemplate[] } | AlimTalkTemplate[]>> => {
    return API.get("/api/v2/notifications/alimtalk-templates/", { params });
  },

  createAlimTalkTemplate: async (
    payload: AlimTalkTemplateUpsertPayload,
  ): Promise<AxiosResponse<AlimTalkTemplate>> => {
    return API.post("/api/v2/notifications/alimtalk-templates/", payload);
  },

  getAlimTalkTemplate: async (id: number): Promise<AxiosResponse<AlimTalkTemplate>> => {
    return API.get(`/api/v2/notifications/alimtalk-templates/${id}/`);
  },

  updateAlimTalkTemplate: async (
    id: number,
    payload: Partial<AlimTalkTemplateUpsertPayload>,
  ): Promise<AxiosResponse<AlimTalkTemplate>> => {
    return API.patch(`/api/v2/notifications/alimtalk-templates/${id}/`, payload);
  },

  syncAlimTalkTemplate: async (id: number): Promise<AxiosResponse<AlimTalkTemplate>> => {
    return API.post(`/api/v2/notifications/alimtalk-templates/${id}/sync/`);
  },

  archiveAlimTalkTemplate: async (id: number): Promise<AxiosResponse<AlimTalkTemplate>> => {
    return API.post(`/api/v2/notifications/alimtalk-templates/${id}/archive/`);
  },

  previewAlimTalkTemplate: async (
    id: number,
    payload: { variables: Record<string, string> },
  ): Promise<AxiosResponse<{ content?: string; buttons?: AlimTalkButton[]; ordered_variables?: string[] }>> => {
    return API.post(`/api/v2/notifications/alimtalk-templates/${id}/preview/`, payload);
  },

  getAlimTalkProviderTemplateCatalog: async (): Promise<AxiosResponse<{
    count: number;
    results: AlimTalkProviderTemplateCatalogItem[];
  }>> => {
    return API.get("/api/v2/notifications/alimtalk-templates/provider-catalog/");
  },

  importAlimTalkProviderTemplates: async (
    templateCodes: string[],
  ): Promise<AxiosResponse<{
    imported_count: number;
    existing_count: number;
    results: Array<{
      template_code: string;
      outcome: "imported" | "already_exists" | string;
      template: AlimTalkTemplate;
    }>;
  }>> => {
    return API.post("/api/v2/notifications/alimtalk-templates/provider-catalog/", {
      template_codes: templateCodes,
    });
  },

  getAlimTalkTemplateBlueprints: async (): Promise<AxiosResponse<{
    count: number;
    results: AlimTalkTemplateBlueprint[];
  }>> => {
    return API.get("/api/v2/notifications/alimtalk-template-blueprints/");
  },

  getAlimTalkWallet: async (): Promise<AxiosResponse<AlimTalkWalletResponse>> => {
    return API.get("/api/v2/notifications/alimtalk-wallet/");
  },

  getAlimTalkTopupRequests: async (params?: NotificationListQuery): Promise<AxiosResponse<{ count?: number; results?: AlimTalkTopupRequest[] } | AlimTalkTopupRequest[]>> => {
    return API.get("/api/v2/notifications/alimtalk-topup-requests/", { params });
  },

  createAlimTalkTopupRequest: async (payload: {
    requested_amount: number;
    depositor_name: string;
    expected_deposit_date?: string;
    memo?: string;
  }, idempotencyKey?: string): Promise<AxiosResponse<AlimTalkTopupRequest>> => {
    return API.post("/api/v2/notifications/alimtalk-topup-requests/", payload, { headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {} });
  },

  cancelAlimTalkTopupRequest: async (id: number): Promise<AxiosResponse<AlimTalkTopupRequest>> => {
    return API.post(`/api/v2/notifications/alimtalk-topup-requests/${id}/cancel/`);
  },

  getAdminAlimTalkTopupRequests: async (params?: NotificationListQuery): Promise<AxiosResponse<{ count?: number; results?: AlimTalkTopupRequest[] } | AlimTalkTopupRequest[]>> => {
    return API.get("/api/v2/notifications/admin/alimtalk-topup-requests/", { params });
  },

  approveAdminAlimTalkTopupRequest: async (
    id: number,
    payload: { approved_amount: number; memo?: string },
  ): Promise<AxiosResponse<AlimTalkTopupRequest>> => {
    return API.post(`/api/v2/notifications/admin/alimtalk-topup-requests/${id}/approve/`, payload);
  },

  rejectAdminAlimTalkTopupRequest: async (
    id: number,
    payload: { reason: string },
  ): Promise<AxiosResponse<AlimTalkTopupRequest>> => {
    return API.post(`/api/v2/notifications/admin/alimtalk-topup-requests/${id}/reject/`, payload);
  },

  createAdminAlimTalkWalletAdjustment: async (payload: {
    site_id: number;
    amount: number;
    reason: string;
  }, idempotencyKey: string): Promise<AxiosResponse<{ site_id: number; wallet: AlimTalkWallet | null; entry_id: number }>> => {
    return API.post("/api/v2/notifications/admin/alimtalk-wallet-adjustments/", payload, {
      headers: { "Idempotency-Key": idempotencyKey },
    });
  },

  getAdminAlimTalkBillingSetting: async (): Promise<AxiosResponse<AlimTalkBillingSetting>> => {
    return API.get("/api/v2/notifications/admin/alimtalk-billing-setting/");
  },

  updateAdminAlimTalkBillingSetting: async (
    payload: Partial<AlimTalkBillingSetting>,
  ): Promise<AxiosResponse<AlimTalkBillingSetting>> => {
    return API.patch("/api/v2/notifications/admin/alimtalk-billing-setting/", payload);
  },

  getAdminAlimTalkMessageRates: async (params?: {
    channel?: "AT" | "AI";
    page?: number;
    page_size?: number;
  }): Promise<AxiosResponse<{ count?: number; results?: AlimTalkMessageRate[] } | AlimTalkMessageRate[]>> => {
    return API.get("/api/v2/messaging/admin/rates/", { params });
  },

  createAdminAlimTalkMessageRate: async (payload: {
    site: number | null;
    channel: "AT" | "AI";
    source?: string | null;
    unit_price: number;
    currency: "KRW";
    is_active: boolean;
    effective_from: string;
    effective_to?: string | null;
    reason: string;
  }): Promise<AxiosResponse<AlimTalkMessageRate>> => {
    return API.post("/api/v2/messaging/admin/rates/", payload);
  },
};
